import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { removeBackground } from "@imgly/background-removal";
import axios from "axios";
import "./UploadPage.css";

export default function UploadPage({ setKiteUrls }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [stream, setStream] = useState(null);
  const [isFrontCamera, setIsFrontCamera] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const navigate = useNavigate();

  // Assign stream to video element AFTER it mounts (fixes black screen)
  useEffect(() => {
    if (isCameraOpen && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch((err) => console.error("Video play error:", err));
    }
  }, [isCameraOpen, stream]);

  // Cleanup stream on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [stream]);

  const startCamera = async () => {
    try {
      // Prioritize the back camera for mobile uploads
      let mediaStream;
      let usedFrontCamera = false;
      
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { 
            facingMode: { ideal: "environment" } // "ideal" works better across various mobile browsers than "exact"
          },
          audio: false,
        });
        
        // Check if the browser ignored "ideal: environment" and gave us the front camera anyway
        const videoTrack = mediaStream.getVideoTracks()[0];
        if (videoTrack) {
          const settings = videoTrack.getSettings();
          if (settings.facingMode === "user") {
            usedFrontCamera = true;
          }
        }
      } catch (err) {
        console.warn("Back camera request failed, falling back to any available camera.", err);
        // Fallback if the device completely rejects the environment request
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
        usedFrontCamera = true;
      }

      setIsFrontCamera(usedFrontCamera);
      setStream(mediaStream);
      setIsCameraOpen(true);
    } catch (err) {
      console.error("Error accessing camera:", err);
      alert("Could not access camera. Please allow camera permissions and try again.");
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setIsCameraOpen(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) {
      alert("Camera not ready yet. Please wait a moment.");
      return;
    }
    const video = videoRef.current;

    // Ensure video has actual dimensions (not 0)
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      alert("Camera feed not ready. Please wait a second and try again.");
      return;
    }

    const canvas = canvasRef.current;
    const w = video.videoWidth;
    const h = video.videoHeight;
    canvas.width = w;
    canvas.height = h;
    const context = canvas.getContext("2d");

    if (isFrontCamera) {
      // Un-mirror the front camera: flip horizontally so saved image is correct
      context.translate(w, 0);
      context.scale(-1, 1);
    }

    context.drawImage(video, 0, 0, w, h);

    canvas.toBlob(
      async (blob) => {
        if (!blob) {
          alert("Failed to capture image. Please try again.");
          return;
        }
        const capturedFile = new File([blob], "captured-kite.png", {
          type: "image/png",
        });
        stopCamera();
        await processFile(capturedFile);
      },
      "image/png"
    );
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    await processFile(selectedFile);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type.startsWith("image/")) {
      await processFile(droppedFile);
    } else {
      alert("Please drop a valid image file.");
    }
  };

  const processFile = async (selectedFile) => {
    setLoading(true);
    try {
      // Remove background using @imgly/background-removal
      const resultBlob = await removeBackground(selectedFile, {
        model: "medium",
        output: { type: "blob" },
      });

      // Convert blob to file
      const finalFile = new File([resultBlob], selectedFile.name.replace(/\.[^/.]+$/, "") + ".png", {
        type: "image/png",
      });

      setFile(finalFile);
      setPreview(URL.createObjectURL(resultBlob));
    } catch (err) {
      console.error("Background removal failed:", err);
      alert("Failed to remove background. Please try another image.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!file) return alert("Please choose a kite image before uploading!");

    const formData = new FormData();
    formData.append("kite", file);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}createKite`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.data) {
        setKiteUrls((prev) => [...prev, response.data]);
        alert("Kite uploaded successfully!");
      }

      // Reset after successful upload
      // setFile(null);
      // setPreview(null);
    } catch (err) {
      console.error("Upload failed", err);
      alert("Failed to upload kite. Please try again.");
    }
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
  };

  return (
    <div className="upload-container">
      <div className="upload-header">
        <h4> <img src={"/assets/flying.png"} style={{width:'80px',height:"80px"}}></img><text>Upload Your Kite</text></h4>
      </div>

      <div className="upload-content">
        {!preview ? (
          <div
            className={`upload-area ${dragOver ? "drag-over" : ""} ${loading ? "loading" : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Processing your image...</p>
                <span>Removing background</span>
              </div>
            ) : isCameraOpen ? (
              <div className="camera-view">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="camera-feed"
                  onLoadedMetadata={(e) => e.target.play()}
                  style={{
                    // Mirror the preview for front camera so it feels like a selfie mirror
                    transform: isFrontCamera ? "scaleX(-1)" : "none",
                    width: "100%",
                    maxHeight: "60vh",
                    objectFit: "cover",
                    borderRadius: "8px",
                  }}
                />
                <div className="camera-controls">
                  <button onClick={capturePhoto} className="capture-btn">
                    Capture Photo
                  </button>
                  <button onClick={stopCamera} className="stop-btn">
                    Cancel
                  </button>
                </div>
                <canvas ref={canvasRef} style={{ display: "none" }} />
              </div>
            ) : (
              <>
                <div className="upload-icon"> <img src={"/assets/image-upload.png"} style={{width:'50px',height:"50px"}}></img></div>
                <h3>Drop your kite image here</h3>
                <div className="upload-options">
                  <label className="file-input-label">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="file-input"
                    />
                    Browse Files
                  </label>
                  <span className="or-divider">or</span>
                  <button onClick={startCamera} className="camera-btn">
                    Take Photo
                  </button>
                </div>
                <p className="file-support">Supports: JPG, PNG, WEBP</p>
              </>
            )}
          </div>
        ) : (
          <div className="preview-section">
            <div className="preview-header">
              <h3>Kite Preview</h3>
              <button onClick={handleReset} className="reset-btn">
                Change Image
              </button>
            </div>
            <div className="preview-image-container">
              <img src={preview} alt="Processed kite" className="preview-image" />
              <div className="preview-overlay">
                <span>Ready to upload!</span>
              </div>
            </div>
            <button onClick={handleUpload} className="upload-btn primary">
              <text style={{}}> Upload Kite </text>
              <img src={"/assets/kite-flying.png"} style={{width:'50px',height:"50px"}}></img>
            </button>
          </div>
        )}
      </div>

      {/* <div className="navigation-buttons">
        <button onClick={() => navigate("/display")} className="nav-btn secondary">
          View All Kites
        </button>
      </div> */}
    </div>
  );
}